/**
 * ASTExplorer provides utility methods for navigating and
 * processing an Abstract Syntax Trees (AST).
 */
export default class ASTExplorer {
  /**
   * Traverses the AST starting from a given node, following a specified relationship
   * (e.g., 'parent'). Applies a callback function to each visited node during the
   * traversal.
   *
   * @param {Object} ast - The starting AST node for the traversal.
   * @param {string} acc - The property name used to traverse to the next node
   *                       (e.g., 'parent' for parent nodes).
   * @param {Function} fn - A callback function that is called with each visited node.
   *                        If this function returns `true`, the traversal stops and
   *                        the current node is returned.
   * @returns {Object|null} The first node for which the callback returns `true`, or
   *                        `null` if no such node is found.
   */
  static walk(ast, acc, fn) {
    // Start with a temporary object pointing to 'ast' to include it in the
    // traversal.
    let current = {[acc]: ast};

    // Traverse the AST, checking each node with the callback function.
    while ((current = current[acc])) {
      if(fn(current)) return current;
    }
  }

  /**
   * Traverses the AST using a specified relationship (e.g., parent) and applies
   * a function to each node, collecting the results in an array.
   *
   * @param {Object} ast - The AST node to start from.
   * @param {string} acc - The accumulator specifying the type of traversal (e.g., 'parent').
   * @param {Function} fn - A function that takes an AST node as a parameter and returns a value.
   * @returns {Array} An array of the results after applying the function to each node.
   */
  static walkMap(ast, acc, fn) {
    const result = [];
    ASTExplorer.walk(ast, acc, (node) => {
      result.push(fn(node));
    })
    return result;
  }

  /**
   * Traverses up the AST from a given node, applying a
   * callback function at each node. The traversal stops and
   * returns the current node if the callback returns `true`.
   *
   * @param {Object} ast - The AST node to start from.
   * @param {Function} fn - A callback function that takes an AST node as a parameter and returns a boolean.
   * @returns {Object|null} The first AST node for which the callback returns `true`, or `null` if no such node is found.
   */
  static walkParents(ast, fn) {
    return ASTExplorer.walk(ast, "parent", fn)
  }

  /**
   * Finds and returns the first parent node of a specified
   * type by traversing up the AST.
   *
   * @param {Object} ast - The AST node to start from.
   * @param {string} type - The type of the parent node to find.
   * @returns {Object|null} The first parent node of the specified type, or `null` if no such node is found.
   */
  static findParent(ast, type) {
    return ASTExplorer.walkParents(ast, (node) => node.type === type)
  }

  /**
   * Retrieves the first child token of the 'rootFn' node in
   * the AST.
   *
   * @param {Object} ast - The AST node to start from.
   * @returns {Object|null} The first child token of the 'rootFn' node, or `null` if the 'rootFn' node is not found.
   */
  static rootFnToken(ast) {
    return ASTExplorer.findParent(ast, "rootFn")?.children[0]
  }
}
